import * as React from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { FunctionComponent, useEffect } from "react";

import * as actions from "../../../Actions/Actions";
import {
    IBuxtonSupportTeamMember,
    IStore,
    ITerritory,
    ITerritoryType,
    ITerritoryTypePermission,
    IUser,
    IUserPreferences,
    IUserProfile,
} from "../../../Models/Models";
import { ComponentLoading } from "../../Common/Loading/Loading";
import UserSettings from "./UserSettings";
import { IOktaMFAFactor } from "../../../Models/OktaModels/IOktaMFAFactor";
import { IRootReducer } from "../../../Models/IRootReducer";
import { IUserShareSettings } from "../../../Models/IUserShareSettings";

interface IUserSettingsContainerDispatchProps {
  loadUser: () => void;
  getUserShareSettings: () => void;
  getAllUsers: () => void;
  getUserPreferences: () => void;
  updateUserPreferences: (userPreferences: IUserPreferences) => void;
  getUserFranchiseeStoreInformation: (userID: number) => void;
  getUserFranchiseeTerritoryInformation: (userID: number) => void;
  removeStoreFromFranchiseeUser: (userID: number, storeID: number) => void;
  removeTerritoryFromFranchiseeUser: (
    userID: number,
    territoryID: number
  ) => void;
  updateUserActiveStatus: (userID: number, activeStatus: boolean) => void;
  updateTerritoryTypeAdminOnlyProperty: (territoryType: ITerritoryType) => void;
  updateTerritoryTypeUserPermissions: (
    userID: number,
    territoryTypeID: number,
    canView: boolean,
    canEdit: boolean
  ) => void;
  getTerritoryTypePermissions: (territoryTypeID: number) => void;
  updateUserProfile: (
    profileUpdateObj: IUserProfile,
    shareSettingsUpdateObj: IUserShareSettings
  ) => void;
  updateUserAvatar: (avatarImg: File) => void;
  deleteUserAvatar: (avatarStr: string) => void;
  updateShareImage: (shareImg: File) => void;
  deleteShareImage: (shareStr: string) => void;
  resetPassword: () => void;
  getCompanySupportTeam: () => void;
  getAllCommunitySites: () => void;
  getCompanyTerritoryTypes: () => void;
  updateTerritoryTypeUserPermissionsForAll: (
    territoryPermissions: Array<ITerritoryTypePermission>
  ) => void;
  GetMFAFactors: (oktaUserID: string) => void;
  IssueMFAChallenge: (factor: IOktaMFAFactor) => void;
  IssuePushMFAChallenge: (factor: IOktaMFAFactor) => void;
  setAllMFAFactors: (factors: IOktaMFAFactor[]) => void;
  setSelectedFactorID: (id: string) => void;
  setWaitingForMFAResponse: (waiting: boolean) => void;
  SubmitMFAChallengeResponse: (
    factor: IOktaMFAFactor,
    response: string
  ) => void;
  PollForPushResponse: (factor: IOktaMFAFactor) => void;
  setShowMfaEnrollmentModal: (show: boolean) => void;
  setMfaEnrollmentUpdatedThisSession: (updated: boolean) => void;
  updateSessionTimeoutLength: (cid: number, newTimeoutLength: number) => void;
}

interface IUserSettingsContainerStateProps {
  user: IUser;
  userShareSettings: IUserShareSettings;
  allUsers: Array<IUser>;
  userPreferences: IUserPreferences;
  userFranchiseeStores: Array<IStore>;
  userFranchiseeTerritories: Array<ITerritory>;
  territoryTypePermissions: Array<ITerritoryTypePermission>;
  companySupportTeam: Array<IBuxtonSupportTeamMember>;
  communitySites: Array<IStore>;
  showSplashScreen: boolean;
  allMFAFactors: IOktaMFAFactor[];
  selectedMFAFactorID: string;
  waitingForMFAResponse: boolean;
  mfaResult: string;
  showMfaEnrollmentModal: boolean;
  mfaEnrollmentModalText: string;
  mfaEnrollmentUpdatedThisSession: boolean;
  saveProfileLoading: boolean;
}

type IUserSettingsContainerProps = IUserSettingsContainerStateProps &
  IUserSettingsContainerDispatchProps;

const mapDispatchToProps = (
    dispatch: React.Dispatch<any>
): IUserSettingsContainerDispatchProps => ({
    loadUser: () => dispatch(actions.loadUser()),
    getUserShareSettings: () => dispatch(actions.getUserShareSettings()),
    getAllUsers: () => dispatch(actions.getAllUsers()),
    getUserPreferences: () => dispatch(actions.getUserPreferences()),
    updateUserPreferences: (userPreferences: IUserPreferences) =>
        dispatch(actions.updateUserPreferences(userPreferences)),
    getUserFranchiseeStoreInformation: (userID: number) =>
        dispatch(actions.getUserFranchiseeStoreInformation(userID)),
    getUserFranchiseeTerritoryInformation: (userID: number) =>
        dispatch(actions.getUserFranchiseeTerritoryInformation(userID)),
    removeStoreFromFranchiseeUser: (userID: number, storeID: number): void =>
        dispatch(actions.removeStoreFromFranchiseeUser(userID, storeID)),
    removeTerritoryFromFranchiseeUser: (
        userID: number,
        territoryID: number
    ): void =>
        dispatch(actions.removeTerritoryFromFranchiseeUser(userID, territoryID)),
    updateUserActiveStatus: (userID: number, activeStatus: boolean): void =>
        dispatch(actions.updateUserActiveStatus(userID, activeStatus)),
    getTerritoryTypePermissions: (territoryTypeID: number) =>
        dispatch(actions.getTerritoryTypePermissions(territoryTypeID)),
    updateTerritoryTypeAdminOnlyProperty: (territoryType: ITerritoryType) =>
        dispatch(actions.updateTerritoryTypeAdminOnlyProperty(territoryType)),
    updateTerritoryTypeUserPermissions: (
        userID: number,
        territoryTypeID: number,
        canView: boolean,
        canEdit: boolean
    ) =>
        dispatch(
            actions.updateTerritoryTypeUserPermissions(
                userID,
                territoryTypeID,
                canView,
                canEdit
            )
        ),
    updateUserProfile: (
        profileUpdateObj: IUserProfile,
        shareSettingsUpdateObj: IUserShareSettings
    ) =>
        dispatch(
            actions.updateUserProfile(profileUpdateObj, shareSettingsUpdateObj)
        ),
    updateUserAvatar: (avatarImg: File) =>
        dispatch(actions.updateUserAvatar(avatarImg)),
    deleteUserAvatar: (avatarStr: string) =>
        dispatch(actions.deleteUserAvatar(avatarStr)),
    updateShareImage: (shareImg: File) =>
        dispatch(actions.updateShareImage(shareImg)),
    deleteShareImage: (imgStr: string) =>
        dispatch(actions.deleteShareImage(imgStr)),
    resetPassword: () => dispatch(actions.resetPassword()),
    getCompanySupportTeam: () => dispatch(actions.getCompanySupportTeam()),
    getAllCommunitySites: () => dispatch(actions.getAllCommunitySites()),
    getCompanyTerritoryTypes: () => dispatch(actions.getCompanyTerritoryTypes()),
    updateTerritoryTypeUserPermissionsForAll: (
        territoryPermissions: Array<ITerritoryTypePermission>
    ) =>
        dispatch(
            actions.updateTerritoryTypeUserPermissionsForAll(territoryPermissions)
        ),
    //MFA
    GetMFAFactors: (oktaUserID: string) =>
        dispatch(actions.GetMFAFactors(oktaUserID)),
    IssueMFAChallenge: (factor: IOktaMFAFactor) =>
        dispatch(actions.IssueMFAChallenge(factor)),
    IssuePushMFAChallenge: (factor: IOktaMFAFactor) =>
        dispatch(actions.IssuePushMFAChallenge(factor)),
    setAllMFAFactors: (factors: IOktaMFAFactor[]) =>
        dispatch(actions.setAllMFAFactors(factors)),
    setSelectedFactorID: (id: string) =>
        dispatch(actions.setSelectedFactorID(id)),
    setWaitingForMFAResponse: (waiting: boolean) =>
        dispatch(actions.setWaitingForMFAResponse(waiting)),
    SubmitMFAChallengeResponse: (factor: IOktaMFAFactor, response: string) =>
        dispatch(actions.SubmitMFAChallengeResponse(factor, response)),
    PollForPushResponse: (factor: IOktaMFAFactor) =>
        dispatch(actions.PollForPushResponse(factor)),
    setShowMfaEnrollmentModal: (show: boolean) =>
        dispatch(actions.setShowMfaEnrollmentModal(show)),
    setMfaEnrollmentUpdatedThisSession: (updated: boolean) =>
        dispatch(actions.setMfaEnrollmentUpdatedThisSession(updated)),
    updateSessionTimeoutLength: (cid: number, newTimeoutLength: number) =>
        dispatch(actions.updateSessionTimeoutLength(cid, newTimeoutLength)),
});

const mapStateToProps = (
    state: IRootReducer
): IUserSettingsContainerStateProps => ({
    user: state.user.user,
    userShareSettings: state.user.shareSettings,
    allUsers: state.user.allUsers,
    userPreferences: state.userPreferencesStore.userPreferences,
    userFranchiseeStores: state.userManagement.franchiseeStoreData,
    userFranchiseeTerritories: state.userManagement.franchiseeTerritoryData,
    territoryTypePermissions: state.territoryManagement.territoryTypePermissions,
    companySupportTeam: state.companySupportTeamStore.companySupportTeam,
    communitySites: state.userPreferencesStore.communitySites,
    showSplashScreen: state.loadScout.showSplashScreen,
    allMFAFactors: state.userProfile.allMFAFactors,
    selectedMFAFactorID: state.userProfile.selectedMFAFactorID,
    waitingForMFAResponse: state.userProfile.waitingForMFAResponse,
    mfaResult: state.userProfile.mfaResult,
    showMfaEnrollmentModal: state.userProfile.showMfaEnrollmentModal,
    mfaEnrollmentModalText: state.userProfile.mfaEnrollmentModalText,
    mfaEnrollmentUpdatedThisSession:
    state.userProfile.mfaEnrollmentUpdatedThisSession,
    saveProfileLoading: state.user.saveProfileLoading,
});

const UserSettingsContainer: FunctionComponent<IUserSettingsContainerProps> = (props:IUserSettingsContainerProps) => {
    useEffect(() => {
        if (!props.user) {
            props.loadUser();
        }
        if (!props.allUsers) {
            props.getAllUsers();
        }
        if (!props.userShareSettings) {
            props.getUserShareSettings();
        }
    }, []);

    if (props.user && props.allUsers) {
        const maybeAllUsers: IUser[] = [...props.allUsers];
        /**
       * If the currently logged in user is NOT a setup user, they cannot see the setup user
       */
        if (!props.user.isSetup) {
            for (let i = 0; i < maybeAllUsers.length; i++) {
                if (maybeAllUsers[i].isSetup) {
                    maybeAllUsers.splice(i, 1);
                    break;
                }
            }
        }

        return (
            <UserSettings
                user={props.user}
                userShareSettings={props.userShareSettings}
                allUsers={maybeAllUsers}
                userPreferences={props.userPreferences}
                userFranchiseeStores={props.userFranchiseeStores}
                userFranchiseeTerritories={props.userFranchiseeTerritories}
                territoryTypePermissions={props.territoryTypePermissions}
                companySupportTeam={props.companySupportTeam}
                communitySites={props.communitySites}
                getUserPreferences={props.getUserPreferences}
                updateUserPreferences={props.updateUserPreferences}
                getUserFranchiseeStoreInformation={
                    props.getUserFranchiseeStoreInformation
                }
                getUserFranchiseeTerritoryInformation={
                    props.getUserFranchiseeTerritoryInformation
                }
                removeStoreFromFranchiseeUser={
                    props.removeStoreFromFranchiseeUser
                }
                removeTerritoryFromFranchiseeUser={
                    props.removeTerritoryFromFranchiseeUser
                }
                updateUserActiveStatus={props.updateUserActiveStatus}
                updateTerritoryTypeAdminOnlyProperty={
                    props.updateTerritoryTypeAdminOnlyProperty
                }
                updateTerritoryTypeUserPermissions={
                    props.updateTerritoryTypeUserPermissions
                }
                getTerritoryTypePermissions={props.getTerritoryTypePermissions}
                updateUserProfile={props.updateUserProfile}
                updateUserAvatar={props.updateUserAvatar}
                deleteUserAvatar={props.deleteUserAvatar}
                updateShareImage={props.updateShareImage}
                deleteShareImage={props.deleteShareImage}
                resetPassword={props.resetPassword}
                getCompanySupportTeam={props.getCompanySupportTeam}
                getAllCommunitySites={props.getAllCommunitySites}
                updateTerritoryTypeUserPermissionsForAll={
                    props.updateTerritoryTypeUserPermissionsForAll
                }
                getCompanyTerritoryTypes={props.getCompanyTerritoryTypes}
                GetMFAFactors={props.GetMFAFactors}
                IssueMFAChallenge={props.IssueMFAChallenge}
                IssuePushMFAChallenge={props.IssuePushMFAChallenge}
                allMFAFactors={props.allMFAFactors}
                selectedMFAFactorID={props.selectedMFAFactorID}
                waitingForMFAResponse={props.waitingForMFAResponse}
                mfaResult={props.mfaResult}
                setAllMFAFactors={props.setAllMFAFactors}
                setSelectedFactorID={props.setSelectedFactorID}
                setWaitingForMFAResponse={props.setWaitingForMFAResponse}
                SubmitMFAChallengeResponse={props.SubmitMFAChallengeResponse}
                PollForPushResponse={props.PollForPushResponse}
                showMfaEnrollmentModal={props.showMfaEnrollmentModal}
                mfaEnrollmentModalText={props.mfaEnrollmentModalText}
                setShowMfaEnrollmentModal={props.setShowMfaEnrollmentModal}
                mfaEnrollmentUpdatedThisSession={
                    props.mfaEnrollmentUpdatedThisSession
                }
                setMfaEnrollmentUpdatedThisSession={
                    props.setMfaEnrollmentUpdatedThisSession
                }
                saveProfileLoading={props.saveProfileLoading}
                updateSessionTimeoutLength={props.updateSessionTimeoutLength}
            />
        );
    }

    return (
        <>
            {!props.user || !props.allUsers && (
                <ComponentLoading title="Loading User Preferences" />
            )}
        </>
    );
};

export default withRouter(
    connect<
    IUserSettingsContainerStateProps,
    IUserSettingsContainerDispatchProps,
    any
  >(
      mapStateToProps,
      mapDispatchToProps
  )(UserSettingsContainer)
);
